import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import FormLabel from '@material-ui/core/FormLabel'
import LinearProgress from '@material-ui/core/LinearProgress'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog'
import { useNotification } from 'components/WorkspaceEditor/services/notificationService'
import { onEscKeyPressOnDialog } from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import React from 'react'
import {
    PT_ACTIVATION_ERRORS,
    PT_DIALOG_CONTENT,
    PT_ERROR_MESSAGES,
    PT_SUCCESS_MESSAGES,
} from '../constants'
import { usePTActivationStore } from '../store'

/**
 * Shared styles for PT activation/deactivation dialogs
 */
const useStyles = makeStyles((theme: Theme) => ({
    dialogTitle: {
        '& .MuiTypography-h6': {
            color: '#304959',
            fontSize: pxToRem(34),
        },
        '&.MuiDialogTitle-root': {
            padding: theme.spacing(2, 0),
        },
        maxWidth: pxToRem(510),
    },

    dialogWrapper: {
        '& .MuiDialog-paperScrollPaper': {
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
            width: pxToRem(560),
            minHeight: pxToRem(203),
        },
    },
    dialogContent: {
        padding: theme.spacing(1, 0),
        overflow: 'hidden',
        color: theme.palette.common.black,
        fontSize: pxToRem(14),
        borderTop: 'none',
        borderBottom: 'none',
        maxWidth: pxToRem(480),
    },
    closeProductTypeDialogActionText: {
        fontSize: pxToRem(18),
        color: '#1899D4',
        fontWeight: 500,
        cursor: 'pointer',
    },
    dialogAction: {
        '&.MuiDialogActions-root': {
            padding: theme.spacing(2.5, 2.5, 3),
        },
    },
    progressContainer: {
        padding: theme.spacing(2, 0, 3),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    progressText: {
        fontSize: pxToRem(13),
        color: 'rgba(0, 0, 0, 0.54)',
        marginBottom: theme.spacing(1),
    },
    progressBar: {
        background: '#9AC4E8',
        border: '1px solid #9AC4E8',
        boxSizing: 'border-box',
        borderRadius: '100px',
        height: 10,
        '& .MuiLinearProgress-bar1Indeterminate': {
            background: '#006FC6',
            borderRadius: '100px',
        },
        '& .MuiLinearProgress-bar2Indeterminate': {
            background: '#006FC6',
            borderRadius: '100px',
        },
        marginBottom: theme.spacing(3),
    },
}))

// ============================================================================
// PT Activation Warning Dialog
// ============================================================================

export interface InactivePTActivationWarningProps {
    /**
     * Defines state of confirmation dialog.
     */
    open: boolean

    /**
     * Product Type names to display in the warning as an array
     */
    ptName: string[]

    /**
     * For closing confirmation dialog (Cancel action)
     */
    onClose: VoidFunction

    /**
     * Handler for proceeding with PT activation
     */
    onProceed: () => Promise<void>
}

/**
 * A dialog component for warning users when they attempt to activate content
 * linked to an inactive Product Type. Gives users the option to:
 * - Cancel: Keep content inactive
 * - Yes: Activate the PT and all its linked content
 *
 * This component matches the styling of the MOPT Restriction Dialog.
 */
export const InactivePTActivationWarning = ({
    open,
    ptName,
    onClose,
    onProceed,
}: InactivePTActivationWarningProps) => {
    const classes = useStyles()
    const notification = useNotification()

    // Get progress state from store
    const isProcessing = usePTActivationStore(
        (state) => state.isProgressDialogOpen
    )
    const activatingPTName = usePTActivationStore(
        (state) => state.activatingPTName
    )

    const handleProceed = async () => {
        try {
            await onProceed()
            onClose()

            // Show success notification
            notification.success({
                message: PT_SUCCESS_MESSAGES.ACTIVATED(ptName.length),
            })
        } catch (error) {
            // Extract error message
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Unknown error occurred'

            // Show detailed error notification
            notification.error({
                message: PT_ERROR_MESSAGES.ACTIVATION_FAILED(ptName.length),
                description: `Error: ${errorMessage}.`,
                duration: 5, // Show for 5 seconds
            })
        }
    }

    const handleClose = () => {
        if (!isProcessing) {
            onClose()
        }
    }

    // Progress dialog content (shown during PT activation)
    const progressContent = (
        <div className={classes.progressContainer}>
            <FormLabel className={classes.progressText}>
                Adding {activatingPTName} to project...
            </FormLabel>
            <LinearProgress className={classes.progressBar} color="primary" />
        </div>
    )

    // Warning dialog content (shown before PT activation)
    const warningContent = (
        <>
            <span>{PT_DIALOG_CONTENT.REQUIRES_PT_ADDITION(ptName.length)}</span>
            <br />
            <br />
            <ul style={{ color: 'black' }}>
                {ptName.map((name, index) => (
                    <li key={index}>{name}</li>
                ))}
            </ul>
            <br />
            <span>Are you sure you want to proceed?</span>
        </>
    )

    // Show progress dialog during processing
    if (isProcessing) {
        return (
            <Dialog
                open={open}
                data-testid="inactive-pt-activation-progress"
                onClose={onEscKeyPressOnDialog(() => {})} // Prevent closing during progress
                className={classes.dialogWrapper}
                disableBackdropClick
                disableEscapeKeyDown
            >
                <DialogTitle className={classes.dialogTitle}>
                    Add to Project Progress
                </DialogTitle>
                <DialogContent
                    className={classes.dialogContent}
                    data-testid="dialogContent"
                >
                    {progressContent}
                </DialogContent>
            </Dialog>
        )
    }

    // Show confirmation dialog before processing
    return (
        <ConfirmationDialog
            openDialog={open}
            handleDialogClose={handleClose}
            dialogActionCallback={handleProceed}
            dialogHeading="Clash Detected"
            confirmText="Yes"
            cancelText="Cancel"
            confirmMessage=""
            contentNode={warningContent}
            contentStyles={{ padding: '20px 0' }}
            size="default"
            removeHeaderBorder
        />
    )
}

// ============================================================================
// PT Deactivation Warning Dialog
// ============================================================================

export interface PTDeactivationWarningProps {
    /**
     * Defines state of confirmation dialog.
     */
    open: boolean

    /**
     * For closing confirmation dialog (Cancel action)
     */
    onClose: VoidFunction

    /**
     * Handler for proceeding with content deactivation
     */
    onProceed: () => Promise<void>
}

/**
 * A dialog component for warning users when they attempt to deactivate content
 * linked to an active Product Type. Gives users the option to:
 * - Cancel: Keep content active
 * - Yes: Proceed with deactivation (only deactivates the specific content, NOT the PT)
 *
 * This component matches the styling of the PT Activation Warning Dialog.
 */
export const PTDeactivationWarning = ({
    open,
    onClose,
    onProceed,
}: PTDeactivationWarningProps) => {
    const [isProcessing, setIsProcessing] = React.useState(false)

    const handleProceed = async () => {
        setIsProcessing(true)

        try {
            await onProceed()
            onClose()
        } catch (error) {
            // Error handling - allow finally to reset processing state
        } finally {
            setIsProcessing(false)
        }
    }

    const handleClose = () => {
        // Prevent closing dialog during processing
        if (!isProcessing) {
            onClose()
        }
    }

    // Dialog content
    const warningContent = (
        <>
            <span>
                This content is required based on your selected Product Type.
            </span>
            <br />
            <span>Are you sure you want to inactivate it?</span>
        </>
    )

    return (
        <ConfirmationDialog
            openDialog={open}
            handleDialogClose={handleClose}
            dialogActionCallback={handleProceed}
            dialogHeading="Product Type Automation"
            confirmText={isProcessing ? 'Processing...' : 'Yes'}
            cancelText="Cancel"
            confirmMessage=""
            contentNode={warningContent}
            contentStyles={{ padding: '20px 0' }}
            size="default"
            removeHeaderBorder
        />
    )
}

// ============================================================================
// Contributor Restriction Dialog
// ============================================================================

export interface ContributorRestrictionDialogProps {
    /**
     * Defines state of dialog.
     */
    open: boolean

    /**
     * For closing dialog (Close action)
     */
    onClose: VoidFunction
}

/**
 * A dialog component for informing contributor/reviewer users that they cannot
 * activate or deactivate PT key paragraphs. This is a read-only dialog with
 * only a Close button (no action buttons).
 *
 * This component matches the styling of ProductTypeDeactivationWarning dialog.
 */
export const ContributorRestrictionDialog = ({
    open,
    onClose,
}: ContributorRestrictionDialogProps) => {
    const classes = useStyles()

    return (
        <Dialog
            open={open}
            data-testid="contributorRestrictionWarning"
            onClose={onEscKeyPressOnDialog(onClose)}
            className={classes.dialogWrapper}
        >
            <DialogTitle className={classes.dialogTitle}>
                {PT_ACTIVATION_ERRORS.CONTRIBUTOR_NOT_ALLOWED_TITLE}
            </DialogTitle>
            <DialogContent
                className={classes.dialogContent}
                data-testid="dialogContent"
            >
                {PT_ACTIVATION_ERRORS.CONTRIBUTOR_NOT_ALLOWED_MESSAGE}
            </DialogContent>

            <DialogActions className={classes.dialogAction}>
                <Typography
                    onClick={onClose}
                    className={classes.closeProductTypeDialogActionText}
                    data-testid="closeButtonContributorRestriction"
                >
                    Close
                </Typography>
            </DialogActions>
        </Dialog>
    )
}
